import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const WEEK_DAYS = [
  { value: 'mon', labelKey: 'days.monday' },
  { value: 'tue', labelKey: 'days.tuesday' },
  { value: 'wed', labelKey: 'days.wednesday' },
  { value: 'thu', labelKey: 'days.thursday' },
  { value: 'fri', labelKey: 'days.friday' },
  { value: 'sat', labelKey: 'days.saturday' },
  { value: 'sun', labelKey: 'days.sunday' },
];

function timeToFloat(value) {
  if (!value || !value.includes(':')) return null;
  const [hours, minutes] = value.split(':');
  const parsedHours = Number(hours);
  const parsedMinutes = Number(minutes);
  if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) return null;
  return parsedHours + (parsedMinutes / 60);
}

export default function EnrollPage() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('course');
  const packagePlanId = searchParams.get('package_plan') || searchParams.get('package') || searchParams.get('pricing_group');

  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const defaultDayRows = WEEK_DAYS.reduce((acc, day) => {
    acc[day.value] = {
      enabled: ['mon', 'wed'].includes(day.value),
      start: '18:00',
      end: '19:00',
    };
    return acc;
  }, {});
  const [form, setForm] = useState({
    schedule_start_date: '',
    day_rows: defaultDayRows,
  });
  const selectedSessionCount = WEEK_DAYS.filter((day) => form.day_rows[day.value]?.enabled).length;

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const redirect = courseId ? `/enroll?course=${encodeURIComponent(courseId)}` : '/enroll';
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
      return;
    }
    if (!courseId) {
      setStatus('error');
      setErrorMsg(t('enroll.missing_course'));
    }
  }, [loading, user, courseId, navigate, t]);

  const togglePreferredDay = (dayCode) => {
    setForm((current) => {
      return {
        ...current,
        day_rows: {
          ...current.day_rows,
          [dayCode]: {
            ...current.day_rows[dayCode],
            enabled: !current.day_rows[dayCode]?.enabled,
          },
        },
      };
    });
  };

  const updateDayTime = (dayCode, key, value) => {
    setForm((current) => ({
      ...current,
      day_rows: {
        ...current.day_rows,
        [dayCode]: {
          ...current.day_rows[dayCode],
          [key]: value,
        },
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!courseId) return;

    const selectedSlots = WEEK_DAYS
      .filter((day) => form.day_rows[day.value]?.enabled)
      .map((day) => ({
        day: day.value,
        start_time: form.day_rows[day.value]?.start || '',
        end_time: form.day_rows[day.value]?.end || '',
      }));

    if (!selectedSlots.length) {
      const message = t('enroll.choose_schedule_row');
      setStatus('error');
      setErrorMsg(message);
      toast.error(message);
      return;
    }

    for (const slot of selectedSlots) {
      const startFloat = timeToFloat(slot.start_time);
      const endFloat = timeToFloat(slot.end_time);
      if (startFloat === null || endFloat === null || endFloat <= startFloat) {
        const message = t('enroll.invalid_time_range').replace('{day}', slot.day.toUpperCase());
        setStatus('error');
        setErrorMsg(message);
        toast.error(message);
        return;
      }
    }

    setStatus('submitting');
    setErrorMsg('');
    try {
      const firstSlot = selectedSlots[0];
      await api.post('/student/enroll', {
        course_id: courseId,
        package_plan_id: packagePlanId || undefined,
        sessions_per_week: selectedSlots.length,
        schedule_start_date: form.schedule_start_date || undefined,
        preferred_days: selectedSlots.map((slot) => slot.day),
        preferred_start_time: firstSlot.start_time,
        preferred_end_time: firstSlot.end_time,
        schedule_slots: selectedSlots,
      });
      setStatus('success');
      toast.success(t('enroll.success'));
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.response?.data?.error || t('enroll.failed'));
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <Card className="max-w-lg w-full rounded-2xl">
        <CardContent className="p-6 space-y-5">
          <div className="text-center space-y-2">
            <h1 className="text-xl font-semibold">{t('enroll.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('enroll.schedule_desc')}
            </p>
          </div>

          {status === 'success' ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">{t('enroll.success')}</p>
              <Link to="/dashboard/student">
                <Button className="rounded-xl">{t('enroll.go_dashboard')}</Button>
              </Link>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="rounded-xl border bg-muted/30 px-4 py-3">
                <p className="text-sm font-medium">{t('enroll.sessions_per_week')}</p>
                <p className="mt-1 text-2xl font-semibold">{selectedSessionCount}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('enroll.sessions_per_week_hint')}
                </p>
              </div>

              <label className="block space-y-2">
                <span className="text-sm font-medium">{t('enroll.apply_schedule_after')}</span>
                <Input
                  type="date"
                  value={form.schedule_start_date}
                  onChange={(event) => setForm((current) => ({ ...current, schedule_start_date: event.target.value }))}
                />
              </label>

              <div className="space-y-2">
                <span className="text-sm font-medium">{t('enroll.preferred_weekly_schedule')}</span>
                <div className="overflow-hidden rounded-xl border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">{t('enroll.day')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('enroll.include')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('enroll.start')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('enroll.end')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {WEEK_DAYS.map((day) => {
                        const row = form.day_rows[day.value] || {};
                        return (
                          <tr key={day.value} className="border-t">
                            <td className="px-3 py-2">{t(day.labelKey)}</td>
                            <td className="px-3 py-2">
                              <Checkbox
                                checked={Boolean(row.enabled)}
                                onCheckedChange={() => togglePreferredDay(day.value)}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="time"
                                value={row.start || ''}
                                onChange={(event) => updateDayTime(day.value, 'start', event.target.value)}
                                disabled={!row.enabled}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="time"
                                value={row.end || ''}
                                onChange={(event) => updateDayTime(day.value, 'end', event.target.value)}
                                disabled={!row.enabled}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {status === 'error' && errorMsg ? (
                <p className="text-sm text-destructive">{errorMsg}</p>
              ) : null}

              <div className="flex items-center gap-3 pt-1">
                <Button type="submit" className="rounded-xl" disabled={status === 'submitting' || !courseId}>
                  {status === 'submitting' ? t('enroll.processing') : t('enroll.title')}
                </Button>
                <Link to="/courses">
                  <Button variant="outline" className="rounded-xl">{t('enroll.back_courses')}</Button>
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
